import type { SupabaseClient } from "@supabase/supabase-js";

// Device inventory read model — public.management_device_inventory (one row per enrolled device;
// device trust + push status. user_ref/device_label are already sha-redacted server-side, no raw
// user ids or device ids). Read-only. Defensive select(*) mapping; ordered by last_seen_at
// (confirmed column) so the most recently active devices surface first.

export interface DeviceRow {
  deviceRowId: string;
  membershipId: string | null;
  userRef: string | null;
  memberDisplayName: string | null;
  memberEmail: string | null;
  memberContactPhone: string | null;
  deviceLabel: string | null;
  platform: string | null;
  status: string;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  trustedAt: string | null;
  revokedAt: string | null;
  revokeReason: string | null;
  lastAppVersion: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  isRevoked: boolean;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

// Number/boolean guards mirror the other read models' defensive style. The view exposes no numeric
// columns today (asNumber is kept for parity / future-proofing); `isRevoked` is derived honestly —
// either a raw boolean flag (guarded via === true) or the presence of the redacted revoked_at.
function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asBoolean(value: unknown): boolean {
  return value === true;
}

export function mapDeviceRow(raw: Record<string, unknown>): DeviceRow {
  const revokedAt = asString(raw.revoked_at);
  return {
    deviceRowId: asString(raw.device_row_id) ?? "",
    membershipId: asString(raw.membership_id),
    userRef: asString(raw.user_ref),
    memberDisplayName: asString(raw.member_display_name),
    memberEmail: asString(raw.member_email),
    memberContactPhone: asString(raw.member_contact_phone),
    deviceLabel: asString(raw.device_label),
    platform: asString(raw.platform),
    status: asString(raw.status) ?? "unknown",
    firstSeenAt: asString(raw.first_seen_at),
    lastSeenAt: asString(raw.last_seen_at),
    trustedAt: asString(raw.trusted_at),
    revokedAt,
    revokeReason: asString(raw.revoke_reason),
    lastAppVersion: asString(raw.last_app_version),
    createdAt: asString(raw.created_at),
    updatedAt: asString(raw.updated_at),
    isRevoked: asBoolean(raw.revoked) || revokedAt !== null,
  };
}

// asNumber is part of the defensive toolkit but unused while the view exposes no numeric columns.
void asNumber;

const DEVICE_LIMIT = 200;

/** Fetch the account's device inventory, most recently active first. Throws on read error. */
export async function fetchDeviceInventory(
  client: SupabaseClient,
  accountId: string,
  limit: number = DEVICE_LIMIT,
): Promise<DeviceRow[]> {
  const { data, error } = await client
    .from("management_device_inventory")
    .select("*")
    .eq("account_id", accountId)
    .order("last_seen_at", { ascending: false })
    .limit(limit);
  if (error !== null) {
    throw new Error("Could not load device inventory.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((row) => mapDeviceRow(row as Record<string, unknown>));
}
